/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { initializeIcons } from '@fluentui/react/lib/Icons';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from 'src/App';
import { GlobalHandlerGroup } from 'src/core/types/GlobalHandlerGroup';
import { GlobalState } from 'src/core/types/GlobalState';
import { isDevelopmentEnvironment } from 'src/core/utils/api/environment';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { createDispatcher } from 'src/core/utils/createDispatcher';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

require('src/styles.less');

/**
 * If in dev env, update fav icon from blob.
 */
if (isDevelopmentEnvironment()) {
    document.querySelector<HTMLLinkElement>("link[rel='icon']").href = getAssetsUrl('assets/images/logos/favicon.png');
}

if (getPortalFromUrl().kind === 'languageStudio') {
    document.querySelector<HTMLLinkElement>("link[rel='icon']").href = getAssetsUrl('assets/images/logos/languageStudioFavicon.svg');
    document.title = 'Language Studio - Microsoft Azure';
} else {
    document.title = 'LUIS (Language Understanding) - Cognitive Services - Microsoft';
}

/**
 * Bootstraps the application rendering. Bootstrapping rendering
 * includes tasks like loading icons, styles, assets, etc.
 * Even more, if Webpack HMR is on, then the application is
 * rendered with HMR configs.
 */
export const bootstrapGlobalRender = async (dependencies: GlobalState) => {
    const rootElement = document.getElementById('root');
    const getRenderableApp = (AppComponent: typeof App, componentDependencies: GlobalState) => <AppComponent {...componentDependencies} />;

    /**
     * On production, read icons from web app. On development,
     * we pass an undefined to have the library default to the
     * CDN endpoint.
     *
     * Note: Trailing slash is required by Fabric.
     */
    const baseIconsUrl = !isDevelopmentEnvironment() ? `${getAssetsUrl('assets/icons/')}` : undefined;
    initializeIcons(baseIconsUrl);
    window.FabricConfig.baseIconUrl = baseIconsUrl;
    const root = ReactDOM.createRoot(rootElement);
    root.render(getRenderableApp(App, dependencies));
    // ReactDOM.render(getRenderableApp(App, dependencies), rootElement);
    if (module.hot) {
        /**
         * If HMR is enabled, fire up its module. `accept()` is a callback
         * that fires every time code is updated.
         */
        // eslint-disable-next-line @typescript-eslint/ban-types
        (module.hot as { accept: Function }).accept('src/App', () => {
            // eslint-disable-next-line global-require
            const AppToRender = (require('src/App') as { App: typeof App }).App;

            root.render(
                getRenderableApp(AppToRender, {
                    ...dependencies,
                    dispatcher: createDispatcher<GlobalHandlerGroup>({
                        rootStore: dependencies.rootStore,
                        uiStore: dependencies.uiStore,
                        lifetime: null
                    })
                })
            );
        });
    }
};
